/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  Form,
  TabContainer,
  Nav,
  Tab,
  Offcanvas,
  Modal
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBarcode, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Divider, Menu, MenuItem } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { useQuery } from 'react-query';

import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { TableMoreIcon } from 'assets/img/icons/icons';
import useLightbox from 'hooks/useLightbox';
import Lightbox from 'components/base/LightBox';
import systemProductService from 'service/systemProduct';
import AddProductToStore from './AddProductToStore';
import SupperModal from 'components/modals/SupperModal';
import UpdateProductStore from './UpdateProductStore';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { useAppContext } from 'providers/AppProvider';
import { useWindowWidth } from 'utils/useWindowSize';
import legalEntitySettingsService from 'service/legalEntitySettings';
import DiscountHistoryTable from './DiscountHistoryTable';

export type ActiveType = {
  photo: string;
  products: string;
  barcode: string;
  brend: string;
  category: string;
  price: string;
  remainder: string;
  remainder_type: string;
  country: string;
  measure_unit: string;
  note: string;
  guid: string;
  currency_id: string;
  old_price: string;
  currency: string;
  created_date: string;
  modarated_status: string;
};

const InternetStore = () => {
  const { t, i18n } = useTranslation();
  const windowSize: any = useWindowWidth();
  const url = process.env.REACT_APP_CDN_URL;

  const {
    config: { theme }
  } = useAppContext();

  const [key, setKey] = useState('active');
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [openUpdateProduct, setOpenUpdateProduct] = useState(false);
  const [getActiveName, setAciteveName] = useState<any>('');
  const [getActiveBarcode, setAciteveBarcode] = useState<any>('');
  const [getNotActiveName, setNotAciteveName] = useState<any>('');
  const [getNotActiveBarcode, setNotAciteveBarcode] = useState<any>('');
  const [activePageCount, setActivePageCount] = useState(1);
  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const [notActivePageCount, setNotActivePageCount] = useState(1);
  const [notActiveCurrentPage, setNotActiveCurrentPage] = useState(1);
  const [debouncedActiveBarcode, setDebouncedActiveBarcode] = useState('');
  const [debouncedNotActiveBarcode, setDebouncedNotActiveBarcode] =
    useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);
  const [notActiveLoading, setNotActiveLoading] = useState(false);

  const storeBreadcrumbItems: PageBreadcrumbItem[] = useMemo(() => {
    return [
      {
        label: t('products'),
        active: true
      }
    ];
  }, [t]);

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  //Get Active products
  const {
    data: dataActive,
    refetch: refetchActive,
    isLoading: isLoadingActive,
    isFetching: isFetchingActive
  } = useQuery(
    [
      'GET_LEGAL_SYSTEM_PRODUCT_ACTIVE',
      i18n.language,
      getActiveName,
      debouncedActiveBarcode,
      activeCurrentPage
    ],
    async () => {
      const res = await systemProductService
        .getList({
          offset: pageToOffset(activeCurrentPage, 10),
          limit: 10,
          status: true,
          lang: i18n.language,
          name: getActiveName,
          barcode: debouncedActiveBarcode
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!activeCurrentPage ||
        !!i18n.language ||
        !!debouncedActiveBarcode ||
        !getActiveName ||
        getActiveName?.length > 2
    }
  );

  useEffect(() => {
    if (dataActive) {
      setActivePageCount(Math.ceil(dataActive?.count / 10));
    }
  }, [dataActive]);

  const loadingActive = isLoadingActive || isFetchingActive;

  //Get Not active products
  const {
    data: dataNotAcitve,
    refetch: refetchNotActive,
    isLoading: isLoadingNotActive,
    isFetching: isFetchingNotActive
  } = useQuery(
    [
      'GET_LEGAL_SYSTEM_PRODUCT_NOT_ACTIVE',
      i18n.language,
      getNotActiveName,
      debouncedNotActiveBarcode,
      notActiveCurrentPage
    ],
    async () => {
      const res = await systemProductService
        .getList({
          offset: pageToOffset(notActiveCurrentPage, 10),
          limit: 10,
          status: false,
          lang: i18n.language,
          name: getNotActiveName,
          barcode: debouncedNotActiveBarcode
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!notActiveCurrentPage ||
        !!i18n.language ||
        !getNotActiveName ||
        getNotActiveName?.length > 2 ||
        !!debouncedNotActiveBarcode
    }
  );

  useEffect(() => {
    if (dataNotAcitve) {
      setNotActivePageCount(Math.ceil(dataNotAcitve?.count / 10));
    }
  }, [dataNotAcitve]);

  const loadingNotActive = isLoadingNotActive || isFetchingNotActive;

  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const acitveData: ActiveType[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();
    return (
      dataActive?.items?.map((items: any) => {
        return {
          photo: items?.photos?.[0]
            ? `${process.env.REACT_APP_CDN_URL}${items?.photos?.[0]}`
            : '',
          products: items?.name || '',
          barcode: items?.barcode || '',
          brend: items?.brand?.view_name || '',
          category: items?.category?.view_name || '',
          price: items?.price || 0,
          remainder: items.quantity || 0,
          remainder_type: items?.quantity_view_type || '',
          country: items?.country?.view_name || '',
          measure_unit: items?.measure_unit?.view_name || '',
          currency: items?.currency?.view_name || '',
          currency_id: items?.currency_id || '',
          note: items?.description || '',
          old_price: items?.old_price || 0,
          created_date: formatDateToTimeZone(+items?.created_at, timeZone),
          modarated_status: items?.moderation_status || t('WAIT'),
          guid: items?.id
        };
      }) ?? []
    );
  }, [dataActive, t, i18n?.language]);

  const notAcitveData: ActiveType[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    return (
      dataNotAcitve?.items?.map((items: any) => {
        return {
          photo: items?.photos?.[0]
            ? `${process.env.REACT_APP_CDN_URL}${items?.photos?.[0]}`
            : '',
          products: items?.name || '',
          barcode: items?.barcode || '',
          brend: items?.brand?.view_name || '',
          category: items?.category?.view_name || '',
          price: items?.price || 0,
          remainder: items.quantity || 0,
          remainder_type: items?.quantity_view_type || '',
          country: items?.country?.view_name || '',
          measure_unit: items?.measure_unit?.view_name || '',
          currency: items?.currency?.view_name || '',
          currency_id: items?.currency_id || '',
          note: items?.description || '',
          old_price: items?.old_price || 0,
          created_date: formatDateToTimeZone(+items?.created_at, timeZone),
          modarated_status: items?.moderation_status || t('WAIT'),
          guid: items?.id
        };
      }) ?? []
    );
  }, [dataNotAcitve, t, i18n?.language]);

  const activeTableColumns: ColumnDef<ActiveType>[] = useMemo(
    () => [
      {
        accessorKey: 'order_number',
        enableSorting: false,
        header: '№',
        cell: rowData => {
          const orderNumber =
            rowData.row.index + 1 + (activeCurrentPage - 1) * 10;
          return <div>{orderNumber}</div>;
        },
        meta: {
          headerProps: { style: { width: '5%' } },
          cellProps: { className: 'text-900 p-2' }
        }
      },

      {
        accessorKey: 'photo',
        header: t('photo'),
        cell: rowData => {
          const [attachments] = useState([rowData?.row?.original.photo]);
          const { lightboxProps, openLightbox } = useLightbox(attachments);

          return (
            <>
              {rowData?.row?.original.photo && (
                <div>
                  <Lightbox {...lightboxProps} />

                  {attachments.map((img, index) => (
                    <img
                      key={img}
                      style={{
                        width: '35px',
                        height: '35px',
                        border: '1px solid #8200BF',
                        borderRadius: '100%',
                        cursor: 'pointer'
                      }}
                      src={img}
                      alt=""
                      onClick={() => openLightbox(index + 1)}
                    />
                  ))}
                </div>
              )}
            </>
          );
        },

        meta: {
          cellProps: { className: 'text-900 text-center' }
        }
      },
      {
        accessorKey: 'products',
        header: t('product'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },

      {
        accessorKey: 'barcode',
        header: t('barcode'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'brend',
        header: t('brand'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'category',
        header: t('category'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'currency',
        header: t('currency'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'price',
        header: t('price'),
        cell: rowData => {
          const { old_price, price } = rowData.row.original;
          const showOldPrice = Number(old_price) !== 0;

          return (
            <div className="d-flex flex-column align-items-start">
              <span>{price}</span>
              {showOldPrice && (
                <span
                  style={{
                    textDecoration: 'line-through',
                    color: '#F76659',
                    fontSize: '14px'
                  }}
                >
                  {old_price}
                </span>
              )}
            </div>
          );
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'remainder',
        header: t('remainder'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'remainder_type',
        header: t('remainder_type'),
        cell: rowData => {
          const { remainder_type } = rowData.row.original;
          /* prettier-ignore */
          return (
            <div>
              {remainder_type === 'SALE'
                ? t('sale')
                : remainder_type === 'SYNCHRONOUS'
                  ? t('synchronus')
                  : t('static')}
            </div>
          );
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'country',
        header: t('country'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'measure_unit',
        header: t('measure_unit'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'created_date',
        header: t('created_date'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },

      {
        accessorKey: 'moderated_status',
        header: t('moderated_status'),
        cell: rowData => {
          const { modarated_status } = rowData.row.original;

          const getStatusStyles = (status: string) => {
            switch (status) {
              case 'WAIT':
                return { backgroundColor: '#ffc107', textColor: '#212529' }; // yellow
              case 'APPROVED':
                return { backgroundColor: '#28a745', textColor: '#ffffff' }; // green
              case 'REJECTED':
                return { backgroundColor: '#dc3545', textColor: '#ffffff' }; // red
              default:
                return { backgroundColor: '#6c757d', textColor: '#ffffff' }; // gray
            }
          };

          const { backgroundColor, textColor } =
            getStatusStyles(modarated_status);

          return (
            <span
              style={{
                backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {t(`${modarated_status}`)}
            </span>
          );
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },

      {
        accessorKey: 'note',
        header: t('descriptions'),
        meta: {
          headerProps: { style: { minWidth: 500, width: '30%' } },
          cellProps: { className: 'text-900' }
        }
      },

      {
        id: 'action',
        cell: rowData => {
          const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
          const [isActive, setIsActive] = useState(true);
          const open = Boolean(anchorEl);
          const { guid, products, currency_id, price, remainder_type } =
            rowData.row.original;

          const toggleAcitveData = {
            id: guid,
            name: products,
            currency_id: currency_id,
            price: price,
            quantity_view_type: remainder_type,
            status: false
          };
          const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          const handleChange = () => {
            setAnchorEl(null);
            setOpenUpdateProduct(true);
            setSelectedProductId(guid);
          };

          // const handleDelete = () => {
          //   setAnchorEl(null);
          //   setSelectedProductId(guid);
          //   setOpenSupperModal(true);
          // };

          const handleToggleActive = () => {
            systemProductService
              .updateSystemProduct(toggleAcitveData)
              .then(res => {
                if (res) {
                  refetchActive();
                  refetchNotActive();
                  setIsActive(prev => !prev);
                  setAnchorEl(null);
                }
              });
          };

          const handleShowHistory = () => {
            setAnchorEl(null);
            setShowHistory(true);
            setSelectedProductId(guid);
          };

          return (
            <>
              <Button variant="hover" onClick={handleClick}>
                <TableMoreIcon />
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MenuItem
                  onClick={handleChange}
                  className="d-flex gap-2 align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <FeatherIcon
                    icon="edit-2"
                    className="cursor-pointer"
                    size={18}
                    color="#8200BF"
                  />
                  {t('discount')}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleShowHistory}
                  className="d-flex gap-2 align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <FeatherIcon
                    icon="file"
                    className="cursor-pointer"
                    size={18}
                    color="#8200BF"
                  />
                  {t('discount_history')}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleToggleActive}
                  className="d-flex align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <Form.Check
                    type="switch"
                    onChange={handleToggleActive}
                    checked={isActive}
                    style={{ fontSize: '12px', marginTop: '6px' }}
                  />
                  {isActive ? t('deactive') : t('make_active')}
                </MenuItem>
                {/* <Divider /> */}
                {/* <MenuItem
                  onClick={handleDelete}
                  className="d-flex gap-2 align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <FeatherIcon
                    icon="trash-2"
                    className="cursor-pointer text-danger"
                    size={18}
                  />
                  {t('delete')}
                </MenuItem> */}
              </Menu>
            </>
          );
        },
        meta: {
          headerProps: {
            style: {
              width: '3%',
              position: 'sticky',
              right: 0,
              background: `${theme === 'dark' ? '#141824' : '#fff'}`,
              zIndex: 1
            }
          },
          cellProps: {
            style: {
              position: 'sticky',
              right: 0,
              background: `${theme === 'dark' ? '#141824' : '#fff'}`,
              zIndex: 1,
              borderLeft: '1px solid #ddd'
            },
            className: 'text-900 text-center'
          }
        }
      }
    ],
    [t, theme]
  );

  const notAcitveTableColumns: ColumnDef<ActiveType>[] = useMemo(
    () => [
      {
        accessorKey: 'order_number',
        enableSorting: false,
        header: '№',
        cell: rowData => {
          const orderNumber =
            rowData.row.index + 1 + (notActiveCurrentPage - 1) * 10;
          return <div>{orderNumber}</div>;
        },
        meta: {
          headerProps: { style: { width: '5%' } },
          cellProps: { className: 'text-900 p-2' }
        }
      },
      {
        accessorKey: 'photo',
        header: t('photo'),
        cell: rowData => {
          const [attachments] = useState([rowData?.row?.original.photo]);
          const { lightboxProps, openLightbox } = useLightbox(attachments);

          return (
            <>
              {rowData?.row?.original.photo && (
                <div>
                  <Lightbox {...lightboxProps} />

                  {attachments.map((img, index) => (
                    <img
                      key={img}
                      style={{
                        width: '35px',
                        height: '35px',
                        border: '1px solid #8200BF',
                        borderRadius: '100%',
                        cursor: 'pointer'
                      }}
                      src={img}
                      alt=""
                      onClick={() => openLightbox(index + 1)}
                    />
                  ))}
                </div>
              )}
            </>
          );
        },

        meta: {
          cellProps: { className: 'text-900 text-center' }
        }
      },
      {
        accessorKey: 'products',
        header: t('product'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },

      {
        accessorKey: 'barcode',
        header: t('barcode'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'brend',
        header: t('brand'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'category',
        header: t('category'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'currency',
        header: t('currency'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'price',
        header: t('price'),
        cell: rowData => {
          const { old_price, price } = rowData.row.original;
          const showOldPrice = Number(old_price) !== 0;

          return (
            <div className="d-flex flex-column align-items-start">
              <span>{price}</span>
              {showOldPrice && (
                <span
                  style={{
                    textDecoration: 'line-through',
                    color: '#F76659',
                    fontSize: '14px'
                  }}
                >
                  {old_price}
                </span>
              )}
            </div>
          );
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'remainder',
        header: t('remainder'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'remainder_type',
        header: t('remainder_type'),
        cell: rowData => {
          const { remainder_type } = rowData.row.original;
          /* prettier-ignore */
          return (
            <div>
              {remainder_type === 'SALE'
                ? t('sale')
                : remainder_type === 'SYNCHRONOUS'
                  ? t('synchronus')
                  : t('static')}
            </div>
          );
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'country',
        header: t('country'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'measure_unit',
        header: t('measure_unit'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'created_date',
        header: t('created_date'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'moderated_status',
        header: t('moderated_status'),
        cell: rowData => {
          const { modarated_status } = rowData.row.original;

          const getStatusStyles = (status: string) => {
            switch (status) {
              case 'WAIT':
                return { backgroundColor: '#ffc107', textColor: '#212529' }; // yellow
              case 'APPROVED':
                return { backgroundColor: '#28a745', textColor: '#ffffff' }; // green
              case 'REJECTED':
                return { backgroundColor: '#dc3545', textColor: '#ffffff' }; // red
              default:
                return { backgroundColor: '#6c757d', textColor: '#ffffff' }; // gray
            }
          };

          const { backgroundColor, textColor } =
            getStatusStyles(modarated_status);

          return (
            <span
              style={{
                backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {t(`${modarated_status}`)}
            </span>
          );
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },

      {
        accessorKey: 'note',
        header: t('descriptions'),
        meta: {
          headerProps: { style: { minWidth: 500, width: '30%' } },
          cellProps: { className: 'text-900' }
        }
      },

      {
        id: 'action',
        cell: rowData => {
          const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
          const [isActive, setIsActive] = useState(false);
          const open = Boolean(anchorEl);
          const { guid, products, currency_id, price, remainder_type } =
            rowData.row.original;

          const toggleAcitveData = {
            id: guid,
            name: products,
            currency_id: currency_id,
            price: price,
            quantity_view_type: remainder_type,
            status: true
          };

          const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          const handleChange = () => {
            setAnchorEl(null);
            setOpenUpdateProduct(true);
            setSelectedProductId(guid);
          };

          // const handleDelete = () => {
          //   setAnchorEl(null);
          //   setSelectedProductId(guid);
          //   setOpenSupperModal(true);
          // };

          const handleToggleActive = () => {
            systemProductService
              .updateSystemProduct(toggleAcitveData)
              .then(res => {
                if (res) {
                  refetchActive();
                  refetchNotActive();
                  setIsActive(prev => !prev);
                  setAnchorEl(null);
                }
              });
          };

          const handleShowHistory = () => {
            setAnchorEl(null);
            setShowHistory(true);
            setSelectedProductId(guid);
          };

          return (
            <>
              <Button variant="hover" onClick={handleClick}>
                <TableMoreIcon />
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MenuItem
                  onClick={handleChange}
                  className="d-flex gap-2 align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <FeatherIcon
                    icon="edit-2"
                    className="cursor-pointer"
                    size={18}
                    color="#8200BF"
                  />
                  {t('discount')}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleShowHistory}
                  className="d-flex gap-2 align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <FeatherIcon
                    icon="file"
                    className="cursor-pointer"
                    size={18}
                    color="#8200BF"
                  />
                  {t('discount_history')}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleToggleActive}
                  className="d-flex align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <Form.Check
                    type="switch"
                    onChange={handleToggleActive}
                    checked={isActive}
                    style={{ fontSize: '12px', marginTop: '6px' }}
                  />
                  {isActive ? t('deactive') : t('make_active')}
                </MenuItem>
                {/* <Divider /> */}
                {/* <MenuItem
                  onClick={handleDelete}
                  className="d-flex gap-2 align-items-center"
                  style={{
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: '#000000',
                    fontFamily: 'Inter'
                  }}
                >
                  <FeatherIcon
                    icon="trash-2"
                    className="cursor-pointer text-danger"
                    size={18}
                  />
                  {t('delete')}
                </MenuItem> */}
              </Menu>
            </>
          );
        },
        meta: {
          headerProps: {
            style: {
              width: '3%',
              position: 'sticky',
              right: 0,
              background: `${theme === 'dark' ? '#141824' : '#fff'}`,
              zIndex: 1
            }
          },
          cellProps: {
            style: {
              position: 'sticky',
              right: 0,
              background: `${theme === 'dark' ? '#141824' : '#fff'}`,
              zIndex: 1,
              borderLeft: '1px solid #ddd'
            },
            className: 'text-900 text-center'
          }
        }
      }
    ],
    [t, theme]
  );

  const tableActive = useAdvanceTable({
    data: acitveData,
    columns: activeTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const tableNotAcitve = useAdvanceTable({
    data: notAcitveData,
    columns: notAcitveTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (selectedProductId && confirmToDelete) {
      systemProductService
        .deleteSystemProduct(selectedProductId)
        .finally(() => {
          refetchActive();
          setOpenSupperModal(false);
          setConfirmToDelete(false);
          setSelectedProductId(null);
        });
    }
  }, [confirmToDelete, selectedProductId, refetchActive, refetchNotActive]);

  const handleExportActive = () => {
    setActiveLoading(true);
    const customFilename: string = 'Активные товары.xlsx';

    // Prepare request payload
    const requestData = {
      status: true,
      name: getActiveName,
      barcode: debouncedActiveBarcode,
      lang: i18n.language
    };

    // Fetch report and trigger download
    systemProductService
      ?.getSystemProductExcel(requestData)
      .then((res: any) => {
        setActiveLoading(false);
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((error: any) => {
        setActiveLoading(false);
        console.log('error==>', error);
      });
  };

  const handleExportNotActive = () => {
    setNotActiveLoading(true);
    const customFilename: string = 'Неактивные товары.xlsx';

    // Prepare request payload
    const requestData = {
      status: false,
      name: getNotActiveName,
      barcode: debouncedNotActiveBarcode,
      lang: i18n.language
    };

    // Fetch report and trigger download
    systemProductService
      ?.getSystemProductExcel(requestData)
      .then((res: any) => {
        setNotActiveLoading(false);
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((error: any) => {
        setNotActiveLoading(false);
        console.log('error==>', error);
      });
  };

  return (
    <div>
      <PageBreadcrumb items={storeBreadcrumbItems} />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>{t('products')}</h2>

        <div className="d-flex justify-content-between gap-2">
          <Button
            className="d-flex align-items-center gap-2"
            variant="primary"
            style={{ height: '48px' }}
            onClick={() => setOpenAddProduct(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            {windowSize > 768 && t('add_product')}
          </Button>
        </div>
      </div>

      <TabContainer
        activeKey={key}
        onSelect={(k: string | null) => setKey(k ?? 'active')}
      >
        <Nav variant="underline" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="active">{t('active_products')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="not-active">{t('inactive_products')}</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="active">
            <div className="d-flex gap-2 mb-3">
              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    type="text"
                    placeholder={t('enter_product')}
                    onFocus={e => e.target.select()}
                    onChange={e => setAciteveName(e.target.value)}
                    className="form-icon-input"
                  />
                  <label
                    htmlFor="floatingInputCustom"
                    className="form-icon-label text-700"
                  >
                    {t('enter_product')}
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-900 fs-9 form-icon"
                />
              </div>

              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    type="text"
                    placeholder={t('scan_barcode')}
                    onFocus={e => e.target.select()}
                    onChange={e => setAciteveBarcode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // Prevent form submission or unintended behavior
                        setDebouncedActiveBarcode(getActiveBarcode);
                      }
                    }}
                    className="form-icon-input"
                  />
                  <label
                    htmlFor="floatingInputCustom"
                    className="form-icon-label text-700"
                  >
                    {t('scan_barcode')}
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faBarcode}
                  className="text-900 fs-9 form-icon"
                />
              </div>

              <Button
                style={{ height: '48px' }}
                variant="outline-primary"
                onClick={handleExportActive}
                loading={activeLoading}
                loadingPosition="start"
              >
                <FeatherIcon icon="upload-cloud" className="me-2" size={20} />
                {t('export')}
              </Button>
            </div>
            <AdvanceTableProvider {...tableActive}>
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table fs-9' }}
                  isLoading={loadingActive}
                />
                <AdvanceTableCPagination
                  count={activePageCount}
                  page={activeCurrentPage}
                  setCurrentPage={setActiveCurrentPage}
                />
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
          <Tab.Pane eventKey="not-active">
            <div className="d-flex gap-2 mb-3">
              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    type="text"
                    placeholder={t('enter_product')}
                    onFocus={e => e.target.select()}
                    onChange={e => setNotAciteveName(e.target.value)}
                    className="form-icon-input"
                  />
                  <label
                    htmlFor="floatingInputCustom"
                    className="form-icon-label text-700"
                  >
                    {t('enter_product')}
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-900 fs-9 form-icon"
                />
              </div>

              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    type="text"
                    placeholder={t('scan_barcode')}
                    onFocus={e => e.target.select()}
                    onChange={e => setNotAciteveBarcode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // Prevent form submission or unintended behavior
                        setDebouncedNotActiveBarcode(getNotActiveBarcode);
                      }
                    }}
                    className="form-icon-input"
                  />
                  <label
                    htmlFor="floatingInputCustom"
                    className="form-icon-label text-700"
                  >
                    {t('scan_barcode')}
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faBarcode}
                  className="text-900 fs-9 form-icon"
                />
              </div>

              <Button
                style={{ height: '48px' }}
                variant="outline-primary"
                onClick={handleExportNotActive}
                loading={notActiveLoading}
                loadingPosition="start"
              >
                <FeatherIcon icon="upload-cloud" className="me-2" size={20} />
                {t('export')}
              </Button>
            </div>

            <AdvanceTableProvider {...tableNotAcitve}>
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table fs-9' }}
                  isLoading={loadingNotActive}
                />
                <AdvanceTableCPagination
                  count={notActivePageCount}
                  page={notActiveCurrentPage}
                  setCurrentPage={setNotActiveCurrentPage}
                />
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
        </Tab.Content>
      </TabContainer>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('product')}
        bodyText={t('you_want_delete')}
      />

      <Offcanvas
        show={openAddProduct}
        onHide={() => {
          setOpenAddProduct(false);
          refetchActive();
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('add_product')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AddProductToStore
            setOpenAddProduct={setOpenAddProduct}
            refetchActive={refetchActive}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        show={openUpdateProduct}
        onHide={() => {
          setOpenUpdateProduct(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('discount')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <UpdateProductStore
            selectedProductId={selectedProductId}
            refetchActive={refetchActive}
            refetchNotActive={refetchNotActive}
            setOpenUpdateProduct={setOpenUpdateProduct}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showHistory}
        onHide={() => setShowHistory(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('discount_history')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DiscountHistoryTable selectedProductId={selectedProductId} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InternetStore;
